import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  IconButton,
  Button,
  Tabs,
  Tab,
  Skeleton,
  Stack,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Restaurant as RestaurantIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  Publish as PublishIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import GeneralInfo from '../../../components/dashboard/GeneralInfo';
import Recommendations from '../../../components/dashboard/Recommendations';
import DayPlans from '../../../components/dashboard/DayPlans';
import ShoppingList from '../../../components/dashboard/ShoppingList';
import nutritionService from '../../../services/nutritionService';
import api from '../../../services/api';

/**
 * Vue de visualisation d'un plan hebdomadaire
 *
 * V3.0: Refactoring Mobile-First
 * - Systeme de tabs (Apercu / Repas / Courses)
 * - Header avec navigation retour et bouton modifier
 * - Responsive design
 */
const CoachPlanView = () => {
  const { clientId, weekPlanId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shoppingList, setShoppingList] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);

  useEffect(() => {
    const fetchPlanDetails = async () => {
      try {
        // Recuperer le plan et la liste de courses en parallele
        const [planData, shoppingData] = await Promise.all([
          api.get(`/coach/weekplans/${weekPlanId}`),
          nutritionService.getShoppingListFromPlan(weekPlanId).catch(err => {
            console.warn('Shopping list API not available, will generate locally:', err.message);
            return null;
          })
        ]);

        // Normaliser client -> user pour compatibilite
        if (planData.client && !planData.user) {
          planData.user = planData.client;
        }

        setPlan(planData);

        // Utiliser la liste de courses de l'API si disponible
        if (shoppingData && shoppingData.items) {
          setShoppingList(shoppingData.items);
        } else {
          // Fallback: generer localement depuis les recettes du plan
          const allIngredients = [];
          for (const dayPlan of planData.dayPlans || []) {
            for (const mealKey of ['breakfast', 'morningSnack', 'lunch', 'snack', 'dinner']) {
              const meal = dayPlan.meals?.[mealKey];
              if (meal && meal.ingredients) {
                allIngredients.push(...meal.ingredients);
              }
            }
          }

          // Agreger les ingredients
          const aggregated = {};
          for (const ing of allIngredients) {
            const key = ing.name || ing.ciqual_code;
            if (aggregated[key]) {
              aggregated[key].quantity += ing.quantity || 0;
            } else {
              aggregated[key] = { ...ing };
            }
          }

          setShoppingList(Object.values(aggregated).map(ing => ({
            ingredient: ing.name,
            quantity: Math.round(ing.quantity || 0),
            unit: ing.unit || 'g'
          })));
        }

        setLoading(false);
      } catch (err) {
        console.error('Erreur lors de la recuperation des donnees:', err.message);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchPlanDetails();
  }, [weekPlanId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleEdit = () => {
    navigate(`/coach/plans/edit/${clientId}/${weekPlanId}`);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePublish = async () => {
    try {
      setPublishing(true);
      await api.put(`/coach/weekplans/${weekPlanId}/publish`);
      setPlan(prev => ({ ...prev, status: 'published' }));
      setPublishSuccess(true);
      setTimeout(() => setPublishSuccess(false), 3000);
    } catch (err) {
      console.error('Erreur publication:', err);
      setError(err.message || 'Erreur lors de la publication');
    } finally {
      setPublishing(false);
    }
  };

  // Loading state avec skeleton
  if (loading) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="text" width="60%" height={40} />
        </Stack>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <IconButton onClick={handleBack} sx={{ mb: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const clientName = plan?.user ? `${plan.user.firstName} ${plan.user.lastName}` : 'Client';
  const weekNumber = plan?.weekNumber || '?';
  const isPublished = plan?.status === 'published';

  return (
    <>
      <Helmet>
        <title>{`Programme de ${clientName} - Semaine ${weekNumber}`}</title>
      </Helmet>

      <Box>
        {/* Alerte de succes publication */}
        {publishSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Plan publie avec succes ! Le client peut maintenant le consulter.
          </Alert>
        )}

        {/* Header avec navigation */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h1" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  {clientName}
                </Typography>
                <Chip
                  label={isPublished ? 'Publie' : 'Brouillon'}
                  size="small"
                  color={isPublished ? 'success' : 'warning'}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Semaine {weekNumber}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            {!isPublished && (
              <Button
                variant="contained"
                size="small"
                color="primary"
                startIcon={publishing ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
                onClick={handlePublish}
                disabled={publishing}
              >
                {isMobile ? '' : 'Publier'}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              {isMobile ? '' : 'Modifier'}
            </Button>
          </Stack>
        </Stack>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={isMobile ? 'fullWidth' : 'standard'}
            sx={{
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontWeight: 500,
              },
            }}
          >
            <Tab
              icon={isMobile ? <PersonIcon /> : undefined}
              iconPosition="start"
              label={isMobile ? '' : 'Apercu'}
              aria-label="Apercu"
            />
            <Tab
              icon={isMobile ? <RestaurantIcon /> : undefined}
              iconPosition="start"
              label={isMobile ? '' : 'Repas'}
              aria-label="Repas"
            />
            <Tab
              icon={isMobile ? <ShoppingCartIcon /> : undefined}
              iconPosition="start"
              label={isMobile ? '' : 'Courses'}
              aria-label="Courses"
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box role="tabpanel">
          {/* Tab 0: Apercu */}
          {activeTab === 0 && (
            <Box>
              <GeneralInfo
                user={plan.user}
                dietaryPreferences={plan.user?.dietaryPreferences}
                goals={plan.user?.goals}
                allergies={plan.user?.intolerances}
                summary={plan.summary}
                targetMacros={plan.user?.targetMacros}
                planId={plan._id}
              />
              <Recommendations recommendations={plan.recommendations} />
            </Box>
          )}

          {/* Tab 1: Repas */}
          {activeTab === 1 && (
            <DayPlans
              dayPlans={plan.dayPlans}
              targetMacros={plan.user?.targetMacros}
              startDate={plan.startDate}
            />
          )}

          {/* Tab 2: Courses */}
          {activeTab === 2 && (
            <ShoppingList shoppingList={shoppingList} />
          )}
        </Box>
      </Box>
    </>
  );
};

export default CoachPlanView;
